import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import ReactMarkdown from 'react-markdown';
import ClientSidebar from '../components/Dashboard/ClientSidebar';
import { userAPI, getFullUrl, dialogsAPI } from '../services/api';
import RatingModal from '../components/Modals/RatingModal';

const Chat = () => {
  const { coachId, dialogId: urlDialogId } = useParams();
  const [coach, setCoach] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [dialogId, setDialogId] = useState(urlDialogId || null);
  const [isCreatingDialog, setIsCreatingDialog] = useState(!urlDialogId);
  const [error, setError] = useState(null);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const { user } = useAuth();
  const [coachRelation, setCoachRelation] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    const fetchCoachAndRelation = async () => {
      try {
        const response = await userAPI.getById(coachId);
        setCoach(response.data.user);

        if (user?.role === 'client') {
          const myCoachesRes = await userAPI.getMyCoaches();
          const myCoaches = Array.isArray(myCoachesRes.data) ? myCoachesRes.data : [];
          const relation = myCoaches.find(c => c.id === coachId);
          if (relation) {
            setCoachRelation(relation);
            if (!relation.isApproved || relation.status !== 'active') {
              setIsBlocked(true);
            }
          } else {
            setIsBlocked(true);
          }
        }
      } catch (err) {
        console.error('Error fetching coach:', err);
        setError('Coach not found');
      }
    };
    if (coachId && user) fetchCoachAndRelation();
  }, [coachId, user]);

  useEffect(() => {
    const initChat = async () => {
      if (!coach || isBlocked) return;
      
      if (urlDialogId) {
        // Load existing history
        setIsLoading(true);
        try {
          const response = await dialogsAPI.getMessages(urlDialogId);
          const items = Array.isArray(response.data) ? response.data : (response.data?.items || []);
          const normalized = items.map(m => ({
            id: m.id,
            text: m.content || m.text,
            sender: m.role || m.sender,
            timestamp: new Date(m.created_at || m.timestamp)
          }));
          normalized.sort((a, b) => a.timestamp - b.timestamp);
          setMessages(normalized);
          setDialogId(urlDialogId);
        } catch (error) {
          console.error('History fetch error:', error);
          if (error.response?.status === 402 || error.response?.data?.error === 'subscription_inactive') {
            setIsBlocked(true);
          } else {
            setError('Failed to load conversation history');
          }
        } finally {
          setIsLoading(false);
          setIsCreatingDialog(false);
        }
      } else {
        // Create new dialog
        setIsCreatingDialog(true);
        try {
          const userId = user?.id || user?.email || 'test-user-123';
          
          const response = await dialogsAPI.create({ 
            coach_id: coach.chatId || coach.id, 
            user_id: userId, 
            metadata: { source: "chat" } 
          });
          
          const data = response.data;
          setDialogId(data.id);
          
          const welcomeMessage = coach.chatWelcomeMessage || `Hello! I'm ${coach.name}, your personal coach. How can I help you today?`;
          setMessages([{ id: 'greeting', text: welcomeMessage, sender: "assistant", timestamp: new Date() }]);
        } catch (error) {
          console.error('Dialog creation error:', error);
          if (error.response?.status === 402 || error.response?.data?.error === 'subscription_inactive') {
            setIsBlocked(true);
          } else {
            setMessages([{ id: 'error', text: error.message || "Connection trouble. Try again later.", sender: "assistant", timestamp: new Date() }]);
          }
        } finally { 
          setIsCreatingDialog(false); 
        }
      }
    };

    if (coach) {
      initChat();
    }
  }, [coach, user, urlDialogId, isBlocked]);

  const sendMessageStream = async (message, onDelta, onComplete) => {
    if (!dialogId) throw new Error('Dialog not created yet');
    
    // We still use raw fetch for streaming since axios doesn't support SSE well in simple setups
    // But we use the token from localStorage
    const token = localStorage.getItem('token');
    const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/dialogs/${dialogId}/messages/stream`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json', 
        'Authorization': `Bearer ${token}` 
      },
      body: JSON.stringify({ content: message }),
    });
    
    if (response.status === 402) {
      setIsBlocked(true);
      throw new Error('Subscription inactive. Please pay to chat with your coach.');
    }
    
    if (!response.ok) throw new Error('Failed to send message');
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop();
      for (const line of lines) {
        if (!line.trim() || !line.startsWith('data: ')) continue;
        try {
          const data = JSON.parse(line.slice(6));
          if (data.type === 'delta') { onDelta(data.content); await new Promise(r => setTimeout(r, 20)); }
          else if (data.type === 'done') onComplete(data.data);
          else if (data.type === 'error') throw new Error(data.message || 'Stream error');
        } catch (e) { console.error('Error parsing stream data:', e); }
      }
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || isLoading || !dialogId || isCreatingDialog) return;
    const userMessage = { id: Date.now(), text: newMessage, sender: "user", timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    const messageText = newMessage;
    setNewMessage('');
    setIsLoading(true);
    try {
      const assistantMessageId = Date.now() + 1;
      setMessages(prev => [...prev, { id: assistantMessageId, text: '', sender: "assistant", timestamp: new Date() }]);
      await sendMessageStream(messageText, (delta) => {
        setMessages(prev => prev.map(msg => msg.id === assistantMessageId ? { ...msg, text: msg.text + delta } : msg));
      }, (finalData) => {
        setMessages(prev => prev.map(msg => msg.id === assistantMessageId ? { ...msg, id: finalData.assistant_message.id, timestamp: new Date(finalData.assistant_message.created_at) } : msg));
      });
    } catch (error) {
      if (error.message !== 'Subscription inactive. Please pay to chat with your coach.') {
        setMessages(prev => [...prev, { id: Date.now(), text: "Connection trouble. Try again later.", sender: "assistant", timestamp: new Date() }]);
      }
    } finally { 
      setIsLoading(false); 
      setLastActivity(Date.now());
    }
  };

  // Inactivity check (10 minutes)
  useEffect(() => {
    const checkInactivity = setInterval(() => {
      const tenMinutes = 10 * 60 * 1000;
      if (Date.now() - lastActivity > tenMinutes && dialogId && !isRatingModalOpen) {
        setIsRatingModalOpen(true);
      }
    }, 60000); // Check every minute

    return () => clearInterval(checkInactivity);
  }, [lastActivity, dialogId, isRatingModalOpen]);

  const messagesEndRef = React.useRef(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const formatTime = (timestamp) => {
    if (!(timestamp instanceof Date)) timestamp = new Date(timestamp);
    return timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (error || (!coach && !isCreatingDialog && !isLoading && messages.length === 0)) {
     return <div className="p-12 text-center text-gray-500 font-bold">{error || "Loading..."}</div>;
  }

  return (
    <div className="relative flex size-full min-h-screen flex-col bg-[var(--bg-main)] group/design-root overflow-x-hidden font-display text-[var(--text-primary)]">
      <div className="layout-container flex h-full grow flex-col">
        <div className="px-4 sm:px-6 md:px-8 lg:px-12 flex flex-1 justify-center py-5 w-full">
          <div className="layout-content-container flex flex-col lg:flex-row w-full max-w-[1200px] flex-1 gap-8">

            <main className="flex-1 flex flex-col min-h-0">
              <header className="mb-6 flex items-center justify-between">
                <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)] tracking-tight font-black">Chat with {coach?.name || 'Coach'}</h1>
                {dialogId && !isBlocked && (
                  <button 
                    onClick={() => setIsRatingModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[var(--off-white)] hover:bg-gray-200 text-[var(--text-secondary)] rounded-xl font-bold transition-all text-sm shadow-sm"
                  >
                    <span className="material-symbols-outlined text-xl">done_all</span>
                    Finish Conversation
                  </button>
                )}
              </header>

              {isBlocked ? (
                <div className="bg-[var(--surface-white)] shadow-xl shadow-black/5 border border-[var(--border-light)] rounded-[2.5rem] flex flex-col items-center justify-center p-8 text-center h-[650px] animate-fade-in">
                  <div className="w-20 h-20 bg-orange-500/10 rounded-full flex items-center justify-center text-tangerine mb-6 animate-pulse border border-orange-500/20">
                    <span className="material-symbols-outlined text-4xl">lock</span>
                  </div>
                  <h3 className="text-2xl font-black text-[var(--text-primary)] dark:text-white mb-3">Subscription Inactive</h3>
                  <p className="text-[var(--text-secondary)] dark:text-gray-300 text-sm max-w-md mb-8 leading-relaxed">
                    {coachRelation?.path === 'A' 
                      ? "Your coach covers your access, but their subscription is currently inactive. Please contact your coach to restore access." 
                      : `Your monthly subscription is inactive. Please pay $${coachRelation?.price || '120'}/month to start communicating with your coach's avatar.`}
                  </p>
                  {coachRelation?.path === 'B' && (
                    <a 
                      href="/user/billing"
                      className="inline-flex items-center gap-2 px-8 py-4 bg-tangerine hover:bg-[#d97700] text-white font-black text-sm uppercase tracking-widest rounded-2xl shadow-lg shadow-orange-500/20 transition-all hover:-translate-y-0.5 active:scale-95 animate-fade-in"
                    >
                      <span className="material-symbols-outlined text-lg">credit_card</span>
                      Go to Billing
                    </a>
                  )}
                </div>
              ) : (
                <div className="bg-[var(--surface-white)] shadow-xl shadow-black/5 border border-[var(--border-light)] rounded-[2.5rem] flex flex-col overflow-hidden h-[650px] relative">
                  <div className="flex-1 overflow-y-auto px-6 py-8 space-y-6 scroll-smooth">
                    {messages.map((message) => {
                      if (message.sender === 'assistant' && !message.text && isLoading) return null;
                      const isUser = message.sender === 'user';
                      return (
                        <div key={message.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                          <div className={`flex ${isUser ? 'flex-row-reverse' : 'flex-row'} items-end gap-3 max-w-[85%]`}>
                            {!isUser && (
                              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-[var(--surface-white)] shadow-md shrink-0 bg-[var(--off-white)] flex items-center justify-center">
                                {coach?.avatarUrl ? (
                                  <img src={getFullUrl(coach.avatarUrl)} alt={coach.name} className="w-full h-full object-cover" />
                                ) : (
                                  <span className="material-symbols-outlined text-gray-400">person</span>
                                )}
                              </div>
                            )}
                            <div className={`group relative px-6 py-4 rounded-[2rem] text-sm shadow-sm ${isUser ? 'bg-[var(--tangerine)] text-white rounded-br-none' : 'bg-[var(--off-white)] text-[var(--text-primary)] border border-[var(--border-light)] rounded-bl-none'}`}>
                              <div className="markdown-content leading-relaxed">
                                <ReactMarkdown components={{ p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p> }}>{message.text}</ReactMarkdown>
                              </div>
                              <p className={`text-[10px] font-bold mt-2 uppercase tracking-widest ${isUser ? 'text-white/60' : 'text-[var(--text-secondary)]/60'}`}>{formatTime(message.timestamp)}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {isLoading && messages[messages.length - 1]?.sender === 'user' && (
                      <div className="flex justify-start">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-[var(--surface-white)] shadow-md shrink-0 bg-[var(--off-white)] flex items-center justify-center">
                             {coach?.avatarUrl ? (
                               <img src={getFullUrl(coach.avatarUrl)} alt={coach.name} className="w-full h-full object-cover" />
                             ) : (
                               <span className="material-symbols-outlined text-gray-400">person</span>
                             )}
                          </div>
                          <div className="bg-[var(--off-white)] border border-[var(--border-light)] px-6 py-4 rounded-[2rem] rounded-bl-none">
                            <div className="flex gap-1.5 translate-y-0.5">
                              <div className="w-1.5 h-1.5 bg-[var(--tangerine)] rounded-full animate-bounce"></div>
                              <div className="w-1.5 h-1.5 bg-[var(--tangerine)] rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                              <div className="w-1.5 h-1.5 bg-[var(--tangerine)] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="p-6 bg-[var(--off-white)]/50 border-t border-[var(--border-light)]">
                    <form onSubmit={handleSendMessage} className="relative flex items-center">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder={isCreatingDialog ? 'Initializing...' : 'Type a message...'}
                        disabled={isLoading || isCreatingDialog || !dialogId}
                        className="w-full bg-[var(--surface-white)] border border-[var(--border-light)] rounded-2xl py-4 pl-6 pr-16 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--tangerine)]/20 focus:border-[var(--tangerine)] shadow-inner transition-all hover:border-[var(--tangerine)]/30"
                      />
                      <button
                        type="submit"
                        disabled={isLoading || isCreatingDialog || !dialogId || !newMessage.trim()}
                        className="absolute right-2 inset-y-2 px-4 bg-[var(--tangerine)] hover:bg-[#d97700] text-white rounded-xl shadow-lg shadow-orange-500/20 transition-all hover:scale-105 active:scale-95 disabled:opacity-50 flex items-center justify-center"
                      >
                        <span className="material-symbols-outlined text-xl">send</span>
                      </button>
                    </form>
                  </div>
                </div>
              )}
            </main>

            <ClientSidebar selectedCoach={coach} activeTab="chat" />
          </div>
        </div>
      </div>
      <RatingModal 
        isOpen={isRatingModalOpen} 
        onClose={() => setIsRatingModalOpen(false)} 
        dialogId={dialogId} 
      />
    </div>
  );
};

export default Chat;
